import DashboardBlockHelper from "../helpers/DashboardBlockHelper";

/**
 * Grid, positioning and collision helpers for the dashboard editor canvas.
 */
const dashboardLayoutEditorCanvasGrid = {
    /**
     * Get dashboard canvas grid element.
     */
    getDashboardCanvasGridElement() {
        if (Array.isArray(this.$refs.dashboardCanvasGrid)) {
            return this.$refs.dashboardCanvasGrid[0] || null;
        }

        return this.$refs.dashboardCanvasGrid || null;
    },

    /**
     * Get dashboard cell key.
     *
     * @param {*} column
     * @param {*} row
     */
    getDashboardCellKey(column, row) {
        return `${column}:${row}`;
    },

    /**
     * Add dashboard occupied cells for block.
     *
     * @param {*} occupiedCells
     * @param {*} block
     */
    addDashboardOccupiedCellsForBlock(occupiedCells, block) {
        for (let row = block.row; row < block.row + block.rowSpan; row += 1) {
            for (let column = block.column; column < block.column + block.columnSpan; column += 1) {
                occupiedCells.add(this.getDashboardCellKey(column, row));
            }
        }
    },

    /**
     * Check whether dashboard area blocked.
     *
     * @param {*} occupiedCells
     * @param {*} column
     * @param {*} row
     * @param {*} columnSpan
     * @param {*} rowSpan
     */
    isDashboardAreaBlocked(occupiedCells, column, row, columnSpan, rowSpan) {
        for (let blockRow = row; blockRow < row + rowSpan; blockRow += 1) {
            for (let blockColumn = column; blockColumn < column + columnSpan; blockColumn += 1) {
                if (occupiedCells.has(this.getDashboardCellKey(blockColumn, blockRow))) {
                    return true;
                }
            }
        }

        return false;
    },

    /**
     * Create dashboard drop target.
     *
     * @param {*} column
     * @param {*} row
     * @param {*} columnSpan
     * @param {*} rowSpan
     */
    createDashboardDropTarget(column, row, columnSpan, rowSpan) {
        return {
            column,
            row,
            columnSpan,
            rowSpan,
        };
    },

    /**
     * Get dashboard widgets for breakpoint.
     *
     * @param {*} breakpointId
     */
    getDashboardWidgetsForBreakpoint(breakpointId) {
        const breakpoint = this.breakpoints.find((currentBreakpoint) => currentBreakpoint.id === breakpointId);

        if (!breakpoint) {
            return [];
        }

        const widgets = [];

        breakpoint.widgetPositioning.forEach((positioning, positioningIndex) => {
            const block = DashboardBlockHelper.getBlock(this.blocks, positioning.referenceId);

            if (!block || positioning.hidden) {
                return;
            }

            const columnSpan = Math.min(
                Math.max(Number(positioning.colspan ?? 1), 1),
                this.dashboardGridColumns,
            );
            const rowSpan = Math.min(
                Math.max(Number(positioning.rowSpan ?? 1), 1),
                this.dashboardGridRows,
            );
            const column = Math.min(
                Math.max(Number(positioning.offset ?? 0) + 1, 1),
                this.dashboardGridColumns - columnSpan + 1,
            );
            const row = Math.min(
                Math.max(Number(positioning.row ?? 1), 1),
                this.dashboardGridRows - rowSpan + 1,
            );

            widgets.push({
                ...block,
                tabId: block.tabId || this.getDefaultDashboardTabId(),
                column,
                row,
                columnSpan,
                rowSpan,
                orderIndex: Number(positioning.orderIndex ?? positioningIndex),
            });
        });

        widgets.sort((currentBlock, nextBlock) => {
            if (currentBlock.row !== nextBlock.row) {
                return currentBlock.row - nextBlock.row;
            }

            if (currentBlock.column !== nextBlock.column) {
                return currentBlock.column - nextBlock.column;
            }

            return currentBlock.orderIndex - nextBlock.orderIndex;
        });

        return widgets;
    },

    /**
     * Get dashboard blocks for breakpoint.
     *
     * @param {*} breakpointId
     */
    getDashboardBlocksForBreakpoint(breakpointId) {
        return this.getDashboardWidgetsForBreakpoint(breakpointId);
    },

    /**
     * Get dashboard widget style.
     *
     * @param {*} block
     */
    getDashboardWidgetStyle(block) {
        return {
            gridColumn: `${block.column} / span ${block.columnSpan}`,
            gridRow: `${block.row} / span ${block.rowSpan}`,
        };
    },

    /**
     * Get dashboard block style.
     *
     * @param {*} block
     */
    getDashboardBlockStyle(block) {
        return this.getDashboardWidgetStyle(block);
    },

    /**
     * Check whether render dashboard widget.
     *
     * @param {*} block
     */
    shouldRenderDashboardWidget(block) {
        if (!this.activeBlock) {
            return true;
        }

        if (this.dashboardCanvasDragReferenceId === block.referenceId) {
            return true;
        }

        if (this.dashboardCanvasCommittedReferenceId === block.referenceId) {
            return true;
        }

        return this.activeBlock.referenceId !== block.referenceId;
    },

    /**
     * Check whether render dashboard block.
     *
     * @param {*} block
     */
    shouldRenderDashboardBlock(block) {
        return this.shouldRenderDashboardWidget(block);
    },

    /**
     * Get dashboard pointer position.
     *
     * @param {*} event
     */
    getDashboardPointerPosition(event) {
        const canvasGrid = this.getDashboardCanvasGridElement();

        if (!canvasGrid) {
            return {
                column: 1,
                row: 1,
            };
        }

        const rect = canvasGrid.getBoundingClientRect();
        const columnWidth = rect.width / this.dashboardGridColumns;
        const rowHeight = rect.height / this.dashboardGridRows;
        const rawColumn = Math.floor((event.clientX - rect.left) / columnWidth) + 1;
        const rawRow = Math.floor((event.clientY - rect.top) / rowHeight) + 1;

        return {
            column: Math.min(Math.max(rawColumn, 1), this.dashboardGridColumns),
            row: Math.min(Math.max(rawRow, 1), this.dashboardGridRows),
        };
    },

    /**
     * Get dashboard occupied cells.
     *
     * @param {*} referenceIdToIgnore
     */
    getDashboardOccupiedCells(referenceIdToIgnore = null) {
        if (!this.activeBreakpoint) {
            return new Set();
        }

        return this.getDashboardOccupiedCellsForBreakpoint(
            this.activeBreakpoint.id,
            referenceIdToIgnore,
        );
    },

    /**
     * Get occupied cells for one breakpoint and the currently selected tab.
     * Widgets on another dashboard tab may use the same grid cells, because
     * they are never displayed together.
     *
     * @param {*} breakpointId
     * @param {*} referenceIdToIgnore
     */
    getDashboardOccupiedCellsForBreakpoint(breakpointId, referenceIdToIgnore = null) {
        const occupiedCells = new Set();
        const activeTabId = this.getActiveDashboardTabId();

        for (const block of this.getDashboardWidgetsForBreakpoint(breakpointId)) {
            const tabId = block.tabId || this.getDefaultDashboardTabId();

            if (block.referenceId === referenceIdToIgnore || tabId !== activeTabId) {
                continue;
            }

            this.addDashboardOccupiedCellsForBlock(occupiedCells, block);
        }

        return occupiedCells;
    },

    /**
     * Constrain one requested widget bounds to the unoccupied space available
     * on the grid. Left and top resizing keeps the opposite edge fixed while
     * it backs away from an occupied cell.
     *
     * @param {*} referenceId
     * @param {*} breakpoint
     * @param {*} bounds
     * @param {*} size
     */
    getDashboardAvailableWidgetBounds(referenceId, breakpoint, bounds = {}, size = {}) {
        const positioning = breakpoint.widgetPositioning.find((item) => item.referenceId === referenceId);

        if (!positioning) {
            return null;
        }

        const currentColumn = Math.max(Number(positioning.offset ?? 0) + 1, 1);
        const currentRow = Math.max(Number(positioning.row ?? 1), 1);
        const currentColumnSpan = Math.max(Number(positioning.colspan ?? 1), 1);
        const currentRowSpan = Math.max(Number(positioning.rowSpan ?? 1), 1);
        const column = Math.min(Math.max(Number(bounds.column ?? currentColumn), 1), this.dashboardGridColumns);
        const row = Math.min(Math.max(Number(bounds.row ?? currentRow), 1), this.dashboardGridRows);
        const maximumColumnSpan = this.dashboardGridColumns - column + 1;
        const maximumRowSpan = this.dashboardGridRows - row + 1;
        const occupiedCells = this.getDashboardOccupiedCellsForBreakpoint(breakpoint.id, referenceId);
        const constrainedBounds = {
            column,
            row,
            columnSpan: Math.min(Math.max(Number(size.columnSpan ?? currentColumnSpan), 1), maximumColumnSpan),
            rowSpan: Math.min(Math.max(Number(size.rowSpan ?? currentRowSpan), 1), maximumRowSpan),
        };

        while (this.isDashboardAreaBlocked(
            occupiedCells,
            constrainedBounds.column,
            constrainedBounds.row,
            constrainedBounds.columnSpan,
            constrainedBounds.rowSpan,
        )) {
            if (size.direction === 'horizontal-left'
                && constrainedBounds.column < currentColumn
                && constrainedBounds.columnSpan > currentColumnSpan) {
                constrainedBounds.column += 1;
                constrainedBounds.columnSpan -= 1;
                continue;
            }

            if ((size.direction === 'horizontal' || size.direction === 'both')
                && constrainedBounds.columnSpan > currentColumnSpan) {
                constrainedBounds.columnSpan -= 1;
                continue;
            }

            if (size.direction === 'vertical-top'
                && constrainedBounds.row < currentRow
                && constrainedBounds.rowSpan > currentRowSpan) {
                constrainedBounds.row += 1;
                constrainedBounds.rowSpan -= 1;
                continue;
            }

            if ((size.direction === 'vertical' || size.direction === 'both')
                && constrainedBounds.rowSpan > currentRowSpan) {
                constrainedBounds.rowSpan -= 1;
                continue;
            }

            return {
                column: currentColumn,
                row: currentRow,
                columnSpan: currentColumnSpan,
                rowSpan: currentRowSpan,
            };
        }

        return constrainedBounds;
    },

    /**
     * Find dashboard drop target.
     *
     * @param {*} column
     * @param {*} row
     * @param {*} block
     * @param {*} referenceIdToIgnore
     */
    findDashboardDropTarget(column, row, block, referenceIdToIgnore = null) {
        const occupiedCells = this.getDashboardOccupiedCells(referenceIdToIgnore);
        const columnSpan = Math.min(Math.max(Number(block.columnSpan ?? block.colspan ?? 1), 1), this.dashboardGridColumns);
        const rowSpan = Math.min(Math.max(Number(block.rowSpan ?? 1), 1), this.dashboardGridRows);
        const normalizedColumn = Math.min(column, this.dashboardGridColumns - columnSpan + 1);
        const normalizedRow = Math.min(row, this.dashboardGridRows - rowSpan + 1);

        for (let currentRow = normalizedRow; currentRow <= this.dashboardGridRows - rowSpan + 1; currentRow += 1) {
            for (let currentColumn = (currentRow === normalizedRow ? normalizedColumn : 1); currentColumn <= this.dashboardGridColumns - columnSpan + 1; currentColumn += 1) {
                if (!this.isDashboardAreaBlocked(occupiedCells, currentColumn, currentRow, columnSpan, rowSpan)) {
                    return this.createDashboardDropTarget(currentColumn, currentRow, columnSpan, rowSpan);
                }
            }
        }

        return null;
    },

    /**
     * Compare dashboard positioning.
     *
     * @param {*} currentPositioning
     * @param {*} nextPositioning
     */
    compareDashboardPositioning(currentPositioning, nextPositioning) {
        const currentRow = Number(currentPositioning.row ?? 1);
        const nextRow = Number(nextPositioning.row ?? 1);

        if (currentRow !== nextRow) {
            return currentRow - nextRow;
        }

        const currentColumn = Number(currentPositioning.offset ?? 0);
        const nextColumn = Number(nextPositioning.offset ?? 0);

        if (currentColumn !== nextColumn) {
            return currentColumn - nextColumn;
        }

        return Number(currentPositioning.orderIndex ?? 0) - Number(nextPositioning.orderIndex ?? 0);
    },

    /**
     * Synchronize dashboard order indexes.
     */
    syncDashboardOrderIndexes() {
        this.breakpoints.forEach((breakpoint) => {
            const sortedPositioning = [...breakpoint.widgetPositioning].sort(this.compareDashboardPositioning);

            sortedPositioning.forEach((positioning, positioningIndex) => {
                positioning.orderIndex = positioningIndex;
            });
        });
    },

    /**
     * Set dashboard widget position.
     *
     * @param {*} referenceId
     * @param {*} target
     */
    setDashboardWidgetPosition(referenceId, target) {
        if (!target) {
            return;
        }

        this.breakpoints.forEach((breakpoint) => {
            const positioning = breakpoint.widgetPositioning.find((currentPositioning) => currentPositioning.referenceId === referenceId);

            if (!positioning) {
                return;
            }

            positioning.offset = target.column - 1;
            positioning.row = target.row;
            positioning.colspan = target.columnSpan;
            positioning.rowSpan = target.rowSpan;
        });

        this.syncDashboardOrderIndexes();
        this.triggerDataProviderEvent('propertyChanged');
    },

    /**
     * Set dashboard widget bounds without allowing it to overlap another
     * visible widget in the selected dashboard tab.
     *
     * @param {*} referenceId
     * @param {*} bounds
     * @param {*} size
     */
    setDashboardWidgetBounds(referenceId, bounds = {}, size = {}) {
        this.breakpoints.forEach((breakpoint) => {
            const positioning = breakpoint.widgetPositioning.find((currentPositioning) => currentPositioning.referenceId === referenceId);

            if (!positioning) {
                return;
            }

            const availableBounds = this.getDashboardAvailableWidgetBounds(referenceId, breakpoint, bounds, size);

            if (!availableBounds) {
                return;
            }

            positioning.offset = availableBounds.column - 1;
            positioning.row = availableBounds.row;
            positioning.colspan = availableBounds.columnSpan;
            positioning.rowSpan = availableBounds.rowSpan;
        });

        this.syncDashboardOrderIndexes();
        this.triggerDataProviderEvent('propertyChanged');
    },

    /**
     * Set dashboard widget size while retaining its current top-left corner.
     *
     * @param {*} referenceId
     * @param {*} size
     */
    setDashboardWidgetSize(referenceId, size) {
        this.setDashboardWidgetBounds(referenceId, {}, size);
    },
};

export default dashboardLayoutEditorCanvasGrid;
